import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Sparkles, MessageCircle, CheckCircle2, CalendarClock } from "lucide-react";

const SUB = {
  Buy: ["Land", "Plot", "Apartment", "Villa"],
  Sell: ["Land", "Plot", "Apartment", "Villa"],
  "Rent/Lease": [
    "Rent your residential property",
    "Rent your commercial property",
    "Lease your commercial property",
    "Looking for residential space for rent",
    "Looking for commercial space for rent",
    "Looking for commercial space for lease",
  ],
};
const CC = [
  { c: "+91", n: "IN" }, { c: "+1", n: "US" }, { c: "+44", n: "UK" },
  { c: "+971", n: "AE" }, { c: "+65", n: "SG" }, { c: "+61", n: "AU" },
];
const DELAY_MS = 60_000;
const SEEN_KEY = "acrepulse_popup_seen_v1";

export default function EngagementPopup() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ intent: "", sub_type: "", full_name: "", country_code: "+91", phone: "" });
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("form"); // form | done
  const [result, setResult] = useState(null);  // { whatsapp_url, calendar_url }

  useEffect(() => {
    if (sessionStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem(SEEN_KEY, "1");
    }, DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    // Reset for next open if any
    setTimeout(() => { setStage("form"); setResult(null); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.intent || !form.sub_type || !form.full_name || !form.phone) {
      toast.error("Please fill all fields");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...form, source: "popup_60s", status: "new" });
      setResult(data);
      setStage("done");
      toast.success("Thank you! An AcrePulse advisor will connect with you shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit. Please try again.");
    } finally { setBusy(false); }
  };

  const subOptions = SUB[form.intent] || [];

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
      <DialogContent
        data-testid="engagement-popup"
        className="bg-[#1E293B] border-[#334155] text-white max-w-md p-0 overflow-hidden"
      >
        {stage === "form" ? (
          <>
            <div className="p-6 pb-4 border-b border-[#334155] relative overflow-hidden">
              <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-[#10B981]/20 blur-2xl" />
              <div className="flex items-center gap-2 text-[#10B981] text-xs uppercase tracking-[0.2em]">
                <Sparkles className="h-3.5 w-3.5" /> Free 15-min consultation
              </div>
              <DialogHeader className="mt-2">
                <DialogTitle className="font-display text-2xl tracking-tight">Buying, selling, or renting?</DialogTitle>
              </DialogHeader>
              <p className="mt-2 text-slate-400 text-sm">Share a few details and an AcrePulse advisor will reach out — no pressure, no signup required.</p>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">I want to</Label>
                <Select value={form.intent} onValueChange={(v) => setForm({ ...form, intent: v, sub_type: "" })}>
                  <SelectTrigger data-testid="popup-intent" className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11">
                    <SelectValue placeholder="Buy, Sell, or Rent/Lease" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                    {Object.keys(SUB).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {form.intent && (
                <div className="fade-up">
                  <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">
                    {form.intent === "Rent/Lease" ? "What kind of arrangement?" : `${form.intent} what?`}
                  </Label>
                  <Select value={form.sub_type} onValueChange={(v) => setForm({ ...form, sub_type: v })}>
                    <SelectTrigger data-testid="popup-subtype" className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11">
                      <SelectValue placeholder="Choose one" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                      {subOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Full name</Label>
                <Input data-testid="popup-name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="e.g. Priya Ramesh" className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Contact number</Label>
                <div className="mt-2 flex gap-2">
                  <Select value={form.country_code} onValueChange={(v) => setForm({ ...form, country_code: v })}>
                    <SelectTrigger data-testid="popup-country-code" className="bg-[#0F172A] border-[#334155] text-white h-11 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                      {CC.map((o) => <SelectItem key={o.c} value={o.c}>{o.c} · {o.n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input data-testid="popup-phone" required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, "") })}
                    placeholder="98765 43210" className="bg-[#0F172A] border-[#334155] text-white h-11 flex-1" />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
                <Button data-testid="popup-cancel" type="button" variant="outline"
                  onClick={close}
                  className="flex-1 h-11 rounded-full border-[#334155] bg-transparent text-white hover:bg-[#0F172A] hover:text-white">
                  Cancel &amp; Continue Exploring
                </Button>
                <Button data-testid="popup-submit" type="submit" disabled={busy}
                  className="flex-1 h-11 rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
                  {busy ? "Submitting…" : "Get a call back"}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="p-8 text-center" data-testid="popup-done">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#10B981]/15 grid place-items-center">
              <CheckCircle2 className="h-7 w-7 text-[#10B981]" />
            </div>
            <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight">You're on the list.</h2>
            <p className="mt-2 text-sm text-slate-400">An advisor will call you shortly. In the meantime — jump on WhatsApp for an instant reply or book a slot straight away.</p>
            <div className="mt-6 flex flex-col gap-3">
              <a
                data-testid="popup-whatsapp"
                href={result?.whatsapp_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white font-medium transition-colors"
              >
                <MessageCircle className="h-4 w-4" /> Continue on WhatsApp
              </a>
              <a
                data-testid="popup-calendar"
                href={result?.calendar_url}
                className="inline-flex items-center justify-center gap-2 h-11 rounded-full border border-[#334155] text-white hover:bg-[#0F172A] transition-colors"
              >
                <CalendarClock className="h-4 w-4 text-[#10B981]" /> Book an advisor slot
              </a>
              <button onClick={close} className="mt-1 text-xs text-slate-500 hover:text-slate-300 uppercase tracking-[0.2em]">Continue exploring</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
